import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadProgress } from '../../types/file.ts';
import { ViewModes } from '../../types/view.ts';
import { ProgressBar } from './ProgressBar.tsx';

interface FileDownloadProgressProps {
  fileDownloadProgress: Array<DownloadProgress>;
  isLoadingFiles: boolean;
  activeViewModes?: ViewModes;
}

export const FileDownloadProgress: React.FC<FileDownloadProgressProps> = ({
  fileDownloadProgress,
  isLoadingFiles,
  activeViewModes = { object: false, measurement: false, render: false },
}) => {
  const { t } = useTranslation();
  const [showProgress, setShowProgress] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);

  const MINIMUM_DISPLAY_TIME = 1000; // 1 second

  // Filter files based on view mode
  const filteredFiles = fileDownloadProgress.filter((file) => {
    if (file.fileType === 'OBJECT_3D') {
      // Always show model files
      return true;
    } else if (file.fileType === 'POINTS_MAP') {
      // Show measurement files only when view mode is "measurement"
      return activeViewModes.measurement;
    } else if (file.fileType === 'BAKED_3D') {
      // Show baked model files only when view mode is "render"
      return activeViewModes.render;
    }
    return true;
  });

  // Track when loading starts
  useEffect(() => {
    if (isLoadingFiles && filteredFiles.length > 0) {
      if (!showProgress) {
        setShowProgress(true);
        setStartTime(Date.now());
      }
    } else if (!isLoadingFiles && showProgress && startTime) {
      // Loading is complete, check if minimum time has passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= MINIMUM_DISPLAY_TIME) {
        setShowProgress(false);
        setStartTime(null);
      } else {
        // Wait for the remaining time
        const remainingTime = MINIMUM_DISPLAY_TIME - elapsedTime;
        setTimeout(() => {
          setShowProgress(false);
          setStartTime(null);
        }, remainingTime);
      }
    }
  }, [isLoadingFiles, filteredFiles.length, showProgress, startTime]);

  if (!showProgress) {
    return null;
  }

  const totalBytes = filteredFiles.reduce((sum, f) => sum + f.totalSize, 0);
  const downloadedBytes = filteredFiles.reduce((sum, f) => sum + f.downloadedSize, 0);
  const overallProgress = totalBytes > 0 ? (downloadedBytes / totalBytes) * 100 : 0;

  return <ProgressBar title={t('common.loading')} overallProgress={overallProgress} />;
};
